import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { useSidebar } from '../../contexts/SidebarContext';
import Icon from '../../components/AppIcon';

import { getTranslation } from '../../utils/translations';
import ReminderCampaignList from './components/ReminderCampaignList';
import EscalationRuleBuilder from './components/EscalationRuleBuilder';
import TemplateManager from './components/TemplateManager';
import CollectionTracking from './components/CollectionTracking';

const PaymentReminderSystem = () => {
  const navigate = useNavigate();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const [currentLanguage, setCurrentLanguage] = useState('FR');
  const [activeTab, setActiveTab] = useState('campaigns');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('appLanguage');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  const t = getTranslation(currentLanguage);

  const breadcrumbItems = [
    { label: 'Accueil', path: '/dashboard' },
    { label: 'Facturation', path: '/billing-overview' },
    { label: 'Système de Rappels' }
  ];

  const tabs = [
    { id: 'campaigns', label: 'Campagnes actives', icon: 'ListChecks' },
    { id: 'escalation', label: 'Règles d\'escalade', icon: 'TrendingUp' },
    { id: 'templates', label: 'Templates', icon: 'FileText' },
    { id: 'tracking', label: 'Suivi des collections', icon: 'BarChart3' }
  ];

  return (
    <div className="flex h-screen bg-background">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />
        <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className="container mx-auto px-4 py-6 max-w-7xl">
            <Breadcrumb items={breadcrumbItems} />

            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <Icon name="Bell" size={28} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">
                    Système de Rappels de Paiement
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    Gestion automatisée des paiements en retard avec escalade personnalisée
                  </p>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-card border border-border rounded-lg mb-6">
              <div className="flex overflow-x-auto">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors border-b-2 whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-primary text-primary bg-primary/5'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/30'
                    }`}
                  >
                    <Icon name={tab.icon} size={18} />
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="space-y-6">
              {activeTab === 'campaigns' && <ReminderCampaignList language={currentLanguage} />}
              {activeTab === 'escalation' && <EscalationRuleBuilder language={currentLanguage} />}
              {activeTab === 'templates' && <TemplateManager language={currentLanguage} />}
              {activeTab === 'tracking' && <CollectionTracking language={currentLanguage} />}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default PaymentReminderSystem;